import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Button } from "@mui/material";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import NoRecord from "../../../assets/SharedComponents/Table/NoRecord";
import {
  REFERENCE_LANGUAGES,
  REFERENCE_RESOURCES,
  buildQuery,
  humanize,
  toastError,
  toastSuccess,
} from "../helpers";
import { LoadingRow } from "./Shared";

/**
 * The controlled vocabularies a dealer must map their DMS to. Dealers read them from
 * GET /api/dealer/v1/reference/{resource} with their own key; this tab serves the same lists
 * through the admin API so support can answer "which id is this colour?" without a dealer key
 * and without opening the database.
 */
const ReferenceTab = () => {
  const [resource, _resource] = useState(REFERENCE_RESOURCES[1].value);
  const [lang, _lang] = useState("en");
  const [productTypeId, _productTypeId] = useState("");
  const [brandId, _brandId] = useState("");
  const [keyword, _keyword] = useState("");
  const [payload, _payload] = useState(null);
  const [productTypes, _productTypes] = useState([]);
  const [brands, _brands] = useState([]);
  const [loading, _loading] = useState(true);
  const seq = useRef(0);
  const brandSeq = useRef(0);

  const definition = useMemo(
    () => REFERENCE_RESOURCES.find((r) => r.value === resource) ?? REFERENCE_RESOURCES[0],
    [resource]
  );

  const changeProductType = (value) => {
    _productTypeId(value);
    _brandId("");
  };

  const fetchResource = useCallback(
    (name, params) =>
      API.get(`${APP_URLS.DEALER_API}/reference/${name}?${buildQuery({ lang, ...params })}`).then((res) => {
        if (res.data.success !== true) {
          throw new Error(res.data.message);
        }
        return res.data.data;
      }),
    [lang]
  );

  // Product types drive the first filter and never narrow, so they are loaded once per language.
  useEffect(() => {
    fetchResource("product_types", {})
      .then((d) => _productTypes(d.items ?? []))
      .catch(toastError);
  }, [fetchResource]);

  // A brand list belongs to one product type, so a change invalidates the options and not just the
  // chosen brand: left standing, a car brand could still be picked under the bike filter and the
  // panel would then list car models. Cleared here rather than in the select's handler so that
  // arriving at `models` from a product-type-filtered resource clears them too. Its own sequence
  // guard, because two quick changes can resolve out of order and the older response would refill
  // the dropdown with the previous product type's brands.
  useEffect(() => {
    if (definition.filter !== "brand") return;
    const current = ++brandSeq.current;
    _brands([]);
    fetchResource("brands", { product_type_id: productTypeId })
      .then((d) => current === brandSeq.current && _brands(d.items ?? []))
      .catch((error) => current === brandSeq.current && toastError(error));
  }, [fetchResource, definition.filter, productTypeId]);

  useEffect(() => {
    // `models` is the one resource the API refuses without a brand.
    if (definition.filter === "brand" && !brandId) {
      _payload(null);
      _loading(false);
      return;
    }

    const current = ++seq.current;
    _loading(true);
    fetchResource(resource, {
      product_type_id: definition.filter === "product_type" ? productTypeId : "",
      brand_id: definition.filter === "brand" ? brandId : "",
    })
      .then((d) => current === seq.current && _payload(d))
      .catch((error) => {
        if (current !== seq.current) return;
        _payload(null);
        toastError(error);
      })
      .finally(() => current === seq.current && _loading(false));
  }, [fetchResource, resource, definition.filter, productTypeId, brandId]);

  const items = useMemo(() => {
    const all = payload?.items ?? [];
    const needle = keyword.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (i) => String(i.id) === needle || String(i.name ?? "").toLowerCase().includes(needle)
    );
  }, [payload, keyword]);

  // Everything beyond id and name differs per resource, so the extra columns are derived.
  const extraColumns = useMemo(() => {
    const skip = ["id", "name"];
    const first = items[0];
    return first ? Object.keys(first).filter((k) => !skip.includes(k)) : [];
  }, [items]);

  const downloadCsv = () => {
    const columns = ["id", "name", ...extraColumns];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [columns.join(","), ...items.map((i) => columns.map((c) => escape(i[c])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `cartobike-${resource}-${lang}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <p className="text-start muted">
        What a dealer must map their DMS values to. They read the same lists from{" "}
        <span className="mono">GET /api/dealer/v1/reference/{"{resource}"}</span> with their own key; unknown ids are
        refused with <span className="mono">unknown_reference</span>.
      </p>

      <div className="filters">
        <select className="filter-select" value={resource} onChange={(e) => _resource(e.target.value)}>
          {REFERENCE_RESOURCES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>

        {definition.filter === "product_type" && (
          <select className="filter-select" value={productTypeId} onChange={(e) => changeProductType(e.target.value)}>
            <option value="">All product types</option>
            {productTypes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        )}

        {definition.filter === "brand" && (
          <>
            {/* Changing the product type invalidates the chosen brand: a car brand left selected
                would ask for its models under a bike filter. */}
            <select className="filter-select" value={productTypeId} onChange={(e) => changeProductType(e.target.value)}>
              <option value="">All product types</option>
              {productTypes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select className="filter-select" value={brandId} onChange={(e) => _brandId(e.target.value)}>
              <option value="">Pick a brand</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </>
        )}

        <select className="filter-select" value={lang} onChange={(e) => _lang(e.target.value)}>
          {REFERENCE_LANGUAGES.map((l) => (
            <option key={l.value} value={l.value}>
              {l.name}
            </option>
          ))}
        </select>

        <input
          className="filter-input"
          placeholder="Id or name"
          value={keyword}
          onChange={(e) => _keyword(e.target.value)}
        />

        {items.length > 0 && (
          <Button variant="outlined" onClick={downloadCsv} sx={{ textTransform: "none" }}>
            Download CSV
          </Button>
        )}
      </div>

      {definition.filter === "brand" && !brandId && !loading && (
        <p className="text-start muted">Pick a brand to list its models.</p>
      )}

      {payload && (
        <p className="text-start muted">
          {payload.count.toLocaleString()} {payload.count === 1 ? "value" : "values"}
          {keyword.trim() ? ` · ${items.length.toLocaleString()} shown` : ""} · served in {payload.locale}
        </p>
      )}

      <div className="tableContainer">
        <table className="auctionTable">
          <thead className="tableHead">
            <tr className="table-row">
              <th className="time-start">Id</th>
              <th className="time-start">Name</th>
              {extraColumns.map((c) => (
                <th key={c} className="time-start">
                  {humanize(c)}
                </th>
              ))}
              <th className="time-start">Actions</th>
            </tr>
          </thead>
          {loading ? (
            <LoadingRow colSpan={3 + extraColumns.length} />
          ) : (
            <tbody>
              {items.length > 0 ? (
                items.map((item) => (
                  <tr key={`${resource}-${item.id}`} className="table-row">
                    <td className="mono">{item.id}</td>
                    <td>{item.name ?? "-"}</td>
                    {extraColumns.map((c) => (
                      <td key={c}>{item[c] === null || item[c] === undefined || item[c] === "" ? "-" : String(item[c])}</td>
                    ))}
                    <td>
                      <div className="action-btns">
                        <CopyToClipboard text={String(item.id)} onCopy={() => toastSuccess("Id copied")}>
                          <Button variant="outlined">Copy id</Button>
                        </CopyToClipboard>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <NoRecord />
              )}
            </tbody>
          )}
        </table>
      </div>
    </>
  );
};

export default ReferenceTab;
